import { ChatCircle, EnvelopeSimple } from '@phosphor-icons/react';
import { useConnections } from '@membranehq/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from 'src/contexts/NotificationContext';
import { useIntegrationAppProviders } from 'src/hooks/useIntegrationAppProviders';
import {
  Agent,
  FieldOperators,
  getReferencedContextParams,
  PostCallActionType,
  Insight,
  InsightType,
  PostCallEmailConfig,
  PostCallFinalizedAction,
  PostCallSmsConfig,
} from 'src/interfaces/agent.interface';
import { ConditionsList } from 'src/pages/Agents/components/conditions/ConditionsList';
import {
  groupFilterToUiConditions,
  IncompleteConditionsError,
  uiConditionsToGroupFilter,
} from 'src/pages/Agents/components/conditions/mappers';
import {
  coerceAndValidateRight,
  isAllowedOperatorForInsight,
} from 'src/pages/Agents/components/conditions/postCallDataExtractionRules';
import {
  PostCallConditionsMode,
  PostCallDataExtractionCondition,
  UiCondition,
  UiConditionType,
} from 'src/pages/Agents/components/conditions/types';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { toInsightVariable } from 'src/components/insights/insightVariable';
import { extractVariables } from 'src/utils/extractVariables';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../Button';
import { Input } from '../Input';
import { Label } from '../Label';
import { Modal } from '../Modal';
import { PromptTextArea } from '../PromptTextArea';

// Variables available for post-call messages (Email/SMS) that map to Call fields
// Keep names in single-brace form to match current editors
// Ordered by most useful first per UX feedback
export const POST_CALL_ACTION_VARIABLES: string[] = [
  '{transcript}',
  '{human_detection_result}',
  '{post_call_results}',
  '{contact_number}',
  '{agent_number}',
  '{status}',
  '{recording_available}',
  '{stage}',
  '{stage_outcome}',
  '{start_time}',
  '{end_time}',
  '{errors}',
  '{do_not_call_result}',
  '{telephony_provider}',
  '{telephony_account_connection}',
  '{is_outgoing}',
  '{telephony_id}',
  '{telephony_metadata}',
  '{id}',
  '{user_id}',
];

interface PostCallActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: PostCallFinalizedAction | null;
  onSave: (action: PostCallFinalizedAction) => void;
  onDelete?: (id: string) => void;
  disabled?: boolean;
  postCallMode?: PostCallConditionsMode;
  insights?: Insight[];
  agent: Agent;
}

const createEmptyEmailConfig = (): PostCallEmailConfig => ({
  from_email: '',
  to_email: '',
  subject: '',
  body: '',
});

const createEmptySmsConfig = (): PostCallSmsConfig => ({
  from_phone: '',
  to_phone: '',
  body: '',
});

export const PostCallActionModal: React.FC<PostCallActionModalProps> = ({
  isOpen,
  onClose,
  action,
  onSave,
  onDelete,
  disabled = false,
  postCallMode = PostCallConditionsMode.PROMPT,
  insights = [],
  agent,
}) => {
  const { items: connections } = useConnections();
  const { emailProviders, smsProviders } = useIntegrationAppProviders();
  const notification = useNotification();

  const isEditing = !!action;

  // Form state
  const [actionType, setActionType] = useState<PostCallActionType | null>(null);
  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [providerId, setProviderId] = useState('');
  const [emailConfig, setEmailConfig] = useState<PostCallEmailConfig>(
    createEmptyEmailConfig(),
  );
  const [smsConfig, setSmsConfig] = useState<PostCallSmsConfig>(
    createEmptySmsConfig(),
  );
  const [conditions, setConditions] = useState<UiCondition[]>([]);

  // Reset form when modal opens/closes or action changes
  useEffect(() => {
    if (isOpen) {
      if (action) {
        setId(action.id);
        setActionType(action.type);
        setLabel(action.label || '');
        setProviderId(action.provider_config?.destination_id || '');
        setConditions(groupFilterToUiConditions(action.conditions));

        if (action.type === PostCallActionType.EMAIL) {
          setEmailConfig(action.config as PostCallEmailConfig);
          setSmsConfig(createEmptySmsConfig());
        } else {
          setSmsConfig(action.config as PostCallSmsConfig);
          setEmailConfig(createEmptyEmailConfig());
        }
      } else {
        // New action - reset everything
        setId('');
        setActionType(null);
        setLabel('');
        setProviderId('');
        setEmailConfig(createEmptyEmailConfig());
        setSmsConfig(createEmptySmsConfig());
        setConditions([]);
      }
    }
  }, [isOpen, action]);

  const handleSelectActionType = (type: PostCallActionType) => {
    setActionType(type);
    setId(uuidv4());
  };

  const isProviderAuthed = useMemo(
    () =>
      !!connections.find(
        (c) => c.integration?.key === providerId && !c.disconnected,
      ),
    [connections, providerId],
  );

  const providersByValue = useMemo(() => {
    const providers =
      actionType === PostCallActionType.EMAIL ? emailProviders : smsProviders;
    return Object.fromEntries(providers.map((p) => [p.value, p.label]));
  }, [actionType, emailProviders, smsProviders]);

  const selectedProviderOption = useMemo(
    () => ({
      value: providerId,
      label: providersByValue[providerId] ?? 'Select a provider',
    }),
    [providerId, providersByValue],
  );

  const insightVariables = useMemo(
    () =>
      insights
        .map((i) => toInsightVariable(i.id))
        .sort((a, b) => a.localeCompare(b)),
    [insights],
  );

  const contextVariables = useMemo(
    () =>
      getReferencedContextParams(agent)
        .map((param) => `{${param}}`)
        .sort((a, b) => a.localeCompare(b)),
    [agent],
  );

  const postCallActionVariables = useMemo(
    () =>
      Array.from(
        new Set([
          ...contextVariables,
          ...insightVariables,
          ...POST_CALL_ACTION_VARIABLES,
        ]),
      ),
    [contextVariables, insightVariables],
  );

  const handleProviderChange = useCallback(
    (providerOption: { value: string; label?: string }) => {
      setProviderId(providerOption.value);
    },
    [],
  );

  const handleConditionsChange = useCallback((next: UiCondition[]) => {
    setConditions(next);
  }, []);

  const handleSave = () => {
    if (!actionType) return;

    // Validate label
    if (!label.trim()) {
      notification.error('Description is required.');
      return;
    }

    // Validate provider
    if (!providerId.trim()) {
      notification.error(
        `Please select ${actionType === PostCallActionType.EMAIL ? 'an email' : 'an SMS'} provider.`,
      );
      return;
    }
    if (!isProviderAuthed) {
      notification.error(
        `Selected ${actionType === PostCallActionType.EMAIL ? 'email' : 'SMS'} provider is not authenticated.`,
      );
      return;
    }

    // Validate type-specific fields
    if (actionType === PostCallActionType.SMS) {
      if (!smsConfig.from_phone?.trim()) {
        notification.error('From number is required.');
        return;
      }
      if (!smsConfig.to_phone?.trim()) {
        notification.error('To number is required.');
        return;
      }
      if (!smsConfig.body?.trim()) {
        notification.error('SMS message body is required.');
        return;
      }
    } else if (actionType === PostCallActionType.EMAIL) {
      if (!emailConfig.from_email?.trim()) {
        notification.error('From email is required.');
        return;
      }
      if (!emailConfig.to_email?.trim()) {
        notification.error('To email is required.');
        return;
      }
      if (!emailConfig.subject?.trim()) {
        notification.error('Subject line is required.');
        return;
      }
      if (!emailConfig.body?.trim()) {
        notification.error('Email body is required.');
        return;
      }
    }

    // Validate conditions
    for (const c of conditions) {
      if (c.type !== UiConditionType.POST_CALL_DATA_EXTRACTION) continue;

      const cond = c as PostCallDataExtractionCondition;
      if (!cond.analysisItemId?.trim()) {
        notification.error('Please select a call insight.');
        return;
      }

      const insight = insights.find((i) => i.id === cond.analysisItemId);
      if (!insight) {
        notification.error(
          `Call insight "${cond.analysisItemId}" was not found. Please select a valid call insight.`,
        );
        return;
      }

      const operator =
        insight.type === InsightType.BOOLEAN
          ? FieldOperators.EQ
          : cond.operator;

      if (!isAllowedOperatorForInsight(insight, operator)) {
        notification.error(
          `Operator is not valid for "${insight.label || insight.id}". Please select a valid operator.`,
        );
        return;
      }

      const coerced = coerceAndValidateRight({
        item: insight,
        operator,
        rightInput: cond.right,
      });
      if (!coerced.ok) {
        notification.error(coerced.message);
        return;
      }
    }

    let conditionsFilter: ReturnType<typeof uiConditionsToGroupFilter> = null;
    try {
      conditionsFilter = uiConditionsToGroupFilter(conditions, {
        insights,
      });
    } catch (err) {
      if (err instanceof IncompleteConditionsError) {
        notification.error(err.message);
        return;
      }
      notification.error(
        err instanceof Error
          ? err.message
          : 'Please complete or remove incomplete conditions.',
      );
      return;
    }

    const savedAction: PostCallFinalizedAction = {
      id: id || uuidv4(),
      label: label.trim(),
      type: actionType,
      provider_config: { destination_id: providerId },
      config: actionType === PostCallActionType.EMAIL ? emailConfig : smsConfig,
      conditions: conditionsFilter,
    };

    onSave(savedAction);
    onClose();
  };

  const handleDelete = () => {
    if (action && onDelete) {
      onDelete(action.id);
      onClose();
    }
  };

  const getTitle = () => {
    if (!actionType && !isEditing) return 'Add Message';
    if (actionType === PostCallActionType.EMAIL)
      return isEditing ? 'Edit Email' : 'Add Email';
    return isEditing ? 'Edit SMS' : 'Add SMS';
  };

  const getSubtitle = () => {
    if (!actionType) return undefined;
    if (isEditing) {
      return `Update the ${actionType === PostCallActionType.EMAIL ? 'email' : 'SMS'} message below`;
    }
    return `Configure a new post-call ${actionType === PostCallActionType.EMAIL ? 'email' : 'SMS'} message.`;
  };

  const renderActionTypeSelection = () => (
    <div className="space-y-4 py-4">
      <p className="text-body text-neutral-400">
        Choose the type of message you want to send:
      </p>
      <div className="grid grid-cols-2 gap-4">
        <button
          data-testid="button-select-email"
          onClick={() => handleSelectActionType(PostCallActionType.EMAIL)}
          className="flex flex-col items-center gap-3 p-6 rounded-lg border border-neutral-200 hover:border-neutral-300 hover:shadow-sm cursor-pointer bg-neutral-0 transition-all duration-150"
        >
          <div className="p-3 rounded-full bg-primary-100 text-primary-500">
            <EnvelopeSimple className="w-6 h-6" weight="fill" />
          </div>
          <div className="text-center">
            <h3 className="text-body font-medium text-primary-body">Email</h3>
            <p className="text-description text-neutral-400">Send emails</p>
          </div>
        </button>
        <button
          data-testid="button-select-sms"
          onClick={() => handleSelectActionType(PostCallActionType.SMS)}
          className="flex flex-col items-center gap-3 p-6 rounded-lg border border-neutral-200 hover:border-neutral-300 hover:shadow-sm cursor-pointer bg-neutral-0 transition-all duration-150"
        >
          <div className="p-3 rounded-full bg-primary-100 text-primary-500">
            <ChatCircle className="w-6 h-6" weight="fill" />
          </div>
          <div className="text-center">
            <h3 className="text-body font-medium text-primary-body">SMS</h3>
            <p className="text-description text-neutral-400">
              Send text messages
            </p>
          </div>
        </button>
      </div>
    </div>
  );

  const renderEmailFields = () => (
    <div className="space-y-6">
      <ProviderSelect
        providers={emailProviders}
        selectedProvider={selectedProviderOption}
        onChange={handleProviderChange}
        isAuthenticated={isProviderAuthed}
        label="Email Provider"
        htmlFor="email-provider"
        className="w-full bg-neutral-0"
        compact
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="From Email"
          id="email-from"
          placeholder="from@your.company.com"
          type="email"
          value={emailConfig.from_email}
          onChange={(e) =>
            setEmailConfig((prev) => ({ ...prev, from_email: e.target.value }))
          }
          disabled={disabled}
        />
        <Input
          label="To Email"
          type="email"
          id="email-to"
          placeholder="to@your.company.com"
          value={emailConfig.to_email}
          onChange={(e) =>
            setEmailConfig((prev) => ({ ...prev, to_email: e.target.value }))
          }
          disabled={disabled}
        />
      </div>
      <div className="pb-6">
        <Label htmlFor="email-subject">Subject Line</Label>
        <PromptTextArea
          value={emailConfig.subject}
          onChange={(e) =>
            setEmailConfig((prev) => ({ ...prev, subject: e.target.value }))
          }
          variables={Array.from(
            new Set([
              ...postCallActionVariables,
              ...extractVariables(emailConfig.subject || ''),
            ]),
          )}
          placeholder="Inbound call summary"
          rows={1}
          loading={disabled}
          className="resize-none whitespace-nowrap overflow-x-none"
        />
      </div>
      <div className="pb-6">
        <Label>Email Body</Label>
        <PromptTextArea
          value={emailConfig.body}
          onChange={(e) =>
            setEmailConfig((prev) => ({ ...prev, body: e.target.value }))
          }
          variables={Array.from(
            new Set([
              ...postCallActionVariables,
              ...extractVariables(emailConfig.body || ''),
            ]),
          )}
          placeholder={`Agent number: {agent_number}\nContact number: {contact_number}\n\nSummary:\n{post_call_results}\n\nTranscript:\n{transcript}\n\n`}
          rows={8}
          loading={disabled}
        />
      </div>
    </div>
  );

  const renderSmsFields = () => (
    <div className="space-y-6">
      <ProviderSelect
        providers={smsProviders}
        selectedProvider={selectedProviderOption}
        onChange={handleProviderChange}
        isAuthenticated={isProviderAuthed}
        label="SMS Provider"
        htmlFor="sms-provider"
        className="w-full bg-neutral-0"
        compact
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="From Number"
          type="text"
          id="sms-from-phone"
          placeholder="(e.g., {agent_number})"
          value={smsConfig.from_phone}
          onChange={(e) =>
            setSmsConfig((prev) => ({ ...prev, from_phone: e.target.value }))
          }
          disabled={disabled}
        />
        <Input
          label="To Number"
          type="text"
          id="sms-to-phone"
          placeholder="(e.g., {contact_number})"
          value={smsConfig.to_phone}
          onChange={(e) =>
            setSmsConfig((prev) => ({ ...prev, to_phone: e.target.value }))
          }
          disabled={disabled}
        />
      </div>
      <div className="pb-6">
        <Label>SMS Message</Label>
        <PromptTextArea
          value={smsConfig.body}
          onChange={(e) =>
            setSmsConfig((prev) => ({ ...prev, body: e.target.value }))
          }
          variables={Array.from(
            new Set([
              ...postCallActionVariables,
              ...extractVariables(smsConfig.body || ''),
            ]),
          )}
          placeholder={
            'Thank you for calling {first_name}! We will get back to you soon. If you have any questions reply to this message.'
          }
          rows={6}
          loading={disabled}
        />
      </div>
    </div>
  );

  const renderConditions = () => (
    <div className="mt-6 pb-4">
      <ConditionsList
        conditions={conditions}
        onChange={handleConditionsChange}
        disabled={disabled}
        mode={postCallMode}
        insights={insights}
        labels={{
          withConditionsDescription: `All conditions must be satisfied for ${actionType === PostCallActionType.EMAIL ? 'email' : 'SMS'} to send`,
          noConditionsDescription: `Add conditions to control when ${actionType === PostCallActionType.EMAIL ? 'emails' : 'messages'} are sent`,
          noConditionsStateText: `No conditions set. ${actionType === PostCallActionType.EMAIL ? 'Emails' : 'SMS messages'} will be sent after every call.`,
        }}
      />
    </div>
  );

  const renderConfigurationForm = () => (
    <div className="space-y-6 pt-4">
      <Input
        label="Description"
        type="text"
        id="action-label"
        placeholder="e.g., Sales Notifications, Support Alerts"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        disabled={disabled}
      />

      {actionType === PostCallActionType.EMAIL
        ? renderEmailFields()
        : renderSmsFields()}

      {renderConditions()}
    </div>
  );

  const renderFooter = () => (
    <div className="flex justify-between gap-4 pt-4 px-6 pb-6 flex-shrink-0 border-t border-neutral-200">
      <div>
        {isEditing && onDelete && (
          <Button
            variant="outlined"
            color="error"
            data-testid="button-delete-action"
            onClick={handleDelete}
            disabled={disabled}
            size="small"
          >
            Delete {actionType === PostCallActionType.EMAIL ? 'Email' : 'SMS'}
          </Button>
        )}
      </div>
      <div className="flex gap-4">
        <Button
          variant="outlined"
          data-testid="button-cancel"
          onClick={onClose}
          size="small"
        >
          Cancel
        </Button>
        <Button
          data-testid="button-save"
          onClick={handleSave}
          disabled={disabled}
          size="small"
        >
          {isEditing
            ? 'Apply Changes'
            : `Add ${actionType === PostCallActionType.EMAIL ? 'Email' : 'SMS'}`}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      subtitle={getSubtitle()}
      footer={actionType || isEditing ? renderFooter() : undefined}
      showModalBorder
      showClose
    >
      {!actionType && !isEditing
        ? renderActionTypeSelection()
        : renderConfigurationForm()}
    </Modal>
  );
};
